/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { FeatureFlagsState } from '@app/app/user-auth/models/feature-flags-state.model';

/** The app environment options. */
export interface Environment {
  /** Defines if the environment code. */
  env: EnvironmentCode;

  /** True if authentication should be disabled. */
  disableAuth: boolean;

  /** The data provider to use. */
  dataProvider: 'api' | 'mock';

  /** The key to use when integrating with IFramely. */
  iframelyApiKey: string;

  /** The MD5-encoded IFramely API key. See docs: https://iframely.com/docs/allow-origins */
  iframelyEncodedApiKey: string;

  /** The root URL of the API. */
  apiRootUrl: string;

  /** Defines the realm configuration */
  realmUrl: string;
  samlSpHost: string;
  clientId: string;
  nonce: string;

  /** Google Analytics */
  googleAnalyticsPropertyId: string;
  reCaptchaSiteKeyV2: string;
  reCaptchaSiteKeyV3: string;

  /** Sentry Configuration */
  sentryConfiguration: SentryConfiguration | null;

  /** Enables V3 UI features */
  isV3Enabled: boolean;

  /**
   * Optional per-service base-URL overrides for local development. When a field is
   * set, the matching data service targets that base URL instead of `apiRootUrl`,
   * letting you run individual microservices locally without proxying everything.
   * Only populated in `environment.dev-custom.ts` (loaded by `yarn start.dev`).
   */
  localServiceOverrides?: LocalServiceOverrides;

  /**
   * In dev mode, use this if you want to log into a different org than `my.yuna.dev.potential.ly`.
   */
  devDomainOverride?: string;

  /**
   * In dev mode, use this to override some feature flags.
   */
  featureFlagOverrides?: Partial<FeatureFlagsState>;
}

/** Local-only base-URL overrides. Leave a field undefined to use the shared API. */
export interface LocalServiceOverrides {
  /** AI text-analysis microservice — admin debug dashboard (e.g. 'http://localhost:8088'). */
  aiTextAnalysisBaseUrl?: string;

  /** Analytics / reporting-dashboards microservice (e.g. 'http://localhost:8090'). */
  reportingBaseUrl?: string;

  /** Analytics microservice — course/programme analytics: admin courses list, overview, cohorts, grades, engagement, surveys (e.g. 'http://localhost:8090'). */
  analyticsBaseUrl?: string;

  /** Course-setup dashboard / assignments microservice (e.g. 'http://localhost:8080'). */
  courseSetupBaseUrl?: string;

  /** Resource microservice — assessment text-analysis resync/status (e.g. 'http://localhost:8080'). */
  resourceBaseUrl?: string;

  /** Media-upload presign microservice — presign/media-upload endpoint (e.g. 'http://localhost:8080'). */
  mediaUploadBaseUrl?: string;
}

export interface SentryConfiguration {
  dsn: string;
  releaseVersion: string;
  tracesSampleRate: number;
  sendDefaultPii: boolean;
}

export enum EnvironmentCode {
  dev = 'dev',
  devLocal = 'local-dev',
  prod = 'prod',
}
